import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '@features/booking/state';
import { Private } from '@shared/components/common';
import { useBillingReceipt } from '@features/booking/hooks/useBillingReceipt';
import { BILL_TYPE_CONTRACT, type BillType } from '@features/booking/utils/billingReceiptLabels';

/**
 * Billing section for the payment step: either the fiscal receipt selector
 * (Peru always, plus any venue with allowBillingReceiptType on) or the generic
 * billing address. Never both — see useBillingReceipt for why.
 *
 * Reads/writes directly from/to booking state.
 */
export function BillingSection() {
  const { t } = useTranslation('booking');
  const booking = useBooking();
  const { showReceiptSelector, showBillingAddress, showDocumentField, labels } = useBillingReceipt();

  const [billingAddress, setBillingAddress] = useState(booking.billingAddress);
  const [identityDocument, setIdentityDocument] = useState(booking.identityDocument);
  const [billType, setBillType] = useState<BillType>(booking.billType || BILL_TYPE_CONTRACT.personal);
  const [billPersonalName, setBillPersonalName] = useState(booking.billPersonalName);
  const [billPersonalDocument, setBillPersonalDocument] = useState(booking.billPersonalDocument);
  const [billPersonalAddress, setBillPersonalAddress] = useState(booking.billPersonalAddress);
  const [billBusinessName, setBillBusinessName] = useState(booking.billBusinessName);
  const [billBusinessDocument, setBillBusinessDocument] = useState(booking.billBusinessDocument);
  const [billBusinessAddress, setBillBusinessAddress] = useState(booking.billBusinessAddress);

  // Sync to booking state on blur (so payment submit has the data)
  const syncBilling = () => {
    const updates: Record<string, unknown> = {};
    if (showBillingAddress) {
      updates.billingAddress = billingAddress.trim();
    }
    // Owned by the venue's country rather than by either block, so an
    // Argentinian venue keeps filling identityDocument once the receipt
    // selector replaces the address block.
    if (showDocumentField) {
      updates.identityDocument = identityDocument.trim();
    }
    if (showReceiptSelector) {
      updates.billType = billType;
      if (billType === BILL_TYPE_CONTRACT.personal) {
        updates.billPersonalName = billPersonalName.trim();
        updates.billPersonalDocument = billPersonalDocument.trim();
        updates.billPersonalAddress = billPersonalAddress.trim();
      } else {
        updates.billBusinessName = billBusinessName.trim();
        updates.billBusinessDocument = billBusinessDocument.trim();
        updates.billBusinessAddress = billBusinessAddress.trim();
      }
    }
    if (Object.keys(updates).length > 0) {
      booking.updateBooking(updates);
    }
  };

  // The payload builder keys off booking.billType, so a guest who opened the
  // selector and then moved to a slot without a guarantee would still send
  // fiscal data for a form no longer on screen. Clearing it on the way out
  // keeps the payload matching what was actually filled in.
  useEffect(() => {
    if (!showReceiptSelector && booking.billType) {
      booking.updateBooking({
        billType: null,
        billPersonalName: '',
        billPersonalDocument: '',
        billPersonalAddress: '',
        billBusinessName: '',
        billBusinessDocument: '',
        billBusinessAddress: '',
      });
    }
  }, [showReceiptSelector, booking.billType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!showBillingAddress && !showReceiptSelector) return null;

  return (
    // Wrap the entire billing region in <Private> so session-replay tools
    // never capture address / ID / tax-ID / business name. All 8 inputs
    // here collect PII; one annotation on the wrapper masks them all.
    <Private className="space-y-4" onBlur={syncBilling}>
      {/* Generic billing address */}
      {showBillingAddress && (
        <div className="space-y-3">
          <label htmlFor="billing-address" className="text-sm font-medium text-foreground/70">{t('PAYMENT_LABEL_BILLING_ADDRESS')}</label>
          <input
            id="billing-address"
            type="text"
            aria-required="true"
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
            placeholder={t('PERSONALIZE_BILLING_ADDRESS_PLACEHOLDER')}
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
          />
        </div>
      )}

      {/* Identity document — Argentina only, alongside whichever block shows */}
      {showDocumentField && (
        <div className="space-y-3">
          <label htmlFor="identity-document" className="text-sm font-medium text-foreground/70">{t('PAYMENT_LABEL_IDENTITY_NUMBER')}</label>
          <input
            id="identity-document"
            type="text"
            aria-required="true"
            className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
            placeholder={t('PERSONALIZE_IDENTITY_DOCUMENT_PLACEHOLDER')}
            value={identityDocument}
            onChange={(e) => setIdentityDocument(e.target.value)}
          />
        </div>
      )}

      {/* Fiscal receipt selector, labelled per the restaurant's country */}
      {showReceiptSelector && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-foreground/70">{t('SELECT_BILLING_OPTION')}</p>
          <div className="flex items-stretch" role="group" aria-label={t('SELECT_BILLING_OPTION')}>
            <button
              type="button"
              aria-pressed={billType === BILL_TYPE_CONTRACT.personal}
              className={`flex-1 min-w-0 px-4 py-2.5 text-sm leading-snug text-balance rounded-l-xl border transition-all duration-200 ${
                billType === BILL_TYPE_CONTRACT.personal
                  ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                  : 'bg-card text-foreground/70 border-border hover:border-brand-light'
              }`}
              onClick={() => setBillType(BILL_TYPE_CONTRACT.personal)}
            >
              {labels.personalReceipt}
            </button>
            <button
              type="button"
              aria-pressed={billType === BILL_TYPE_CONTRACT.business}
              className={`flex-1 min-w-0 px-4 py-2.5 text-sm leading-snug text-balance rounded-r-xl border transition-all duration-200 ${
                billType === BILL_TYPE_CONTRACT.business
                  ? 'bg-brand text-brand-foreground border-brand shadow-sm'
                  : 'bg-card text-foreground/70 border-border hover:border-brand-light'
              }`}
              onClick={() => setBillType(BILL_TYPE_CONTRACT.business)}
            >
              {labels.businessReceipt}
            </button>
          </div>

          {billType === BILL_TYPE_CONTRACT.personal ? (
            <div className="space-y-3">
              <label htmlFor="bill-personal-name" className="sr-only">{t('SIGNUP_LABEL_NAME')}</label>
              <input
                id="bill-personal-name"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('SIGNUP_LABEL_NAME')}
                value={billPersonalName}
                onChange={(e) => setBillPersonalName(e.target.value)}
              />
              <label htmlFor="bill-personal-document" className="sr-only">{labels.personalDocument}</label>
              <input
                id="bill-personal-document"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={labels.personalDocument}
                value={billPersonalDocument}
                onChange={(e) => setBillPersonalDocument(e.target.value)}
              />
              <label htmlFor="bill-personal-address" className="sr-only">{t('ADDRESS')}</label>
              <input
                id="bill-personal-address"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('ADDRESS')}
                value={billPersonalAddress}
                onChange={(e) => setBillPersonalAddress(e.target.value)}
              />
            </div>
          ) : (
            <div className="space-y-3">
              <label htmlFor="bill-business-name" className="sr-only">{t('BUSINESS_NAME')}</label>
              <input
                id="bill-business-name"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('BUSINESS_NAME')}
                value={billBusinessName}
                onChange={(e) => setBillBusinessName(e.target.value)}
              />
              <label htmlFor="bill-business-document" className="sr-only">{labels.businessDocument}</label>
              <input
                id="bill-business-document"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={labels.businessDocument}
                value={billBusinessDocument}
                onChange={(e) => setBillBusinessDocument(e.target.value)}
              />
              <label htmlFor="bill-business-address" className="sr-only">{t('BUSINESS_ADDRESS')}</label>
              <input
                id="bill-business-address"
                type="text"
                aria-required="true"
                className="w-full rounded-xl border border-border p-3 text-foreground focus:border-brand focus:outline-none"
                placeholder={t('BUSINESS_ADDRESS')}
                value={billBusinessAddress}
                onChange={(e) => setBillBusinessAddress(e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </Private>
  );
}
